import React, { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useTheme } from '../context/ThemeContext';
import { usePurchasedDatasets } from '../hooks/usePurchasedDatasets';
import { createDownloadPackage } from '../lib/callables';
import './DatasetExport.css';

type DatasetExportProps = {
  user: User;
};

export default function DatasetExport({ user }: DatasetExportProps) {
  const { colors } = useTheme();
  const { datasetId } = useParams<{ datasetId: string }>();
  const navigate = useNavigate();
  const { datasets: purchased } = usePurchasedDatasets(user.uid);
  const [dataset, setDataset] = useState<any>(null);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const isPurchased = datasetId ? purchased[datasetId] !== undefined : false;

  useEffect(() => {
    if (!datasetId) {
      navigate('/library');
      return;
    }

    if (!isPurchased) {
      navigate('/library');
      return;
    }

    const loadData = async () => {
      try {
        // Parse datasetId (format: category_subcategory)
        const parts = datasetId.split('_');
        if (parts.length < 2) {
          navigate('/library');
          return;
        }
        const category = parts[0];
        const subcategory = parts.slice(1).join('_'); // Handle subcategories with underscores

        // Load all submissions matching this category and subcategory
        const submissionsRef = collection(db, 'submissions');
        const q = query(
          submissionsRef,
          where('status', '==', 'completed') // Fully approved after labeling
        );
        const submissionsSnap = await getDocs(q);
        
        const allSubmissions: any[] = submissionsSnap.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((sub: any) => {
            const subCategory = sub.category || sub.taskCategory || 'General';
            const subSubcategory = sub.subcategory || sub.taskSubcategory || 'N/A';
            // Only fully labeled
            const labelCount = sub.labelCount || (Array.isArray(sub.labels) ? sub.labels.filter((l: any) => l !== null && l !== undefined).length : 0);
            
            // Match category and subcategory
            return subCategory === category && 
                   subSubcategory === subcategory && 
                   labelCount > 0 && 
                   sub.isFullyLabeled !== false;
          });

        if (allSubmissions.length === 0) {
          navigate('/library');
          return;
        }

        // Build dataset metadata from submissions using aggregation
        const { aggregateDatasetsFromSubmissions } = await import('../utils/firestore');
        const datasets = aggregateDatasetsFromSubmissions(allSubmissions);
        const dataset = datasets.find(ds => ds.datasetId === datasetId);

        if (!dataset) {
          navigate('/library');
          return;
        }

        setDataset({
          id: dataset.datasetId,
          name: `${dataset.category} - ${dataset.subcategory}`,
          category: dataset.category,
          subcategory: dataset.subcategory,
          numImages: dataset.totalImages,
          numLabels: dataset.totalLabels,
          version: '1', // Auto-increment per export (can be enhanced later)
        });

        setSubmissions(allSubmissions);
      } catch (error) {
        console.error('Failed to load dataset export data', error);
        setError('Failed to load dataset');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [datasetId, user.uid, isPurchased, navigate]);

  const handleExport = async () => {
    if (selectedIds.size === 0) {
      setError('Please select at least one submission to export.');
      return;
    }

    setIsExporting(true);
    setError(null);
    setSuccessMessage(null);
    setDownloadUrl(null);

    try {
      const submissionIds = Array.from(selectedIds);
      const result = await createDownloadPackage(submissionIds);
      setSuccessMessage('Export ready! Your ZIP download should begin shortly.');
      setDownloadUrl(result.downloadUrl);
      setSelectedIds(new Set());
      window.open(result.downloadUrl, '_blank');
    } catch (err: any) {
      console.error('Export failed', err);
      setError(err?.message || 'Failed to create export.');
    } finally {
      setIsExporting(false);
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (submissions.every((sub) => selectedIds.has(sub.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(submissions.map((sub) => sub.id)));
    }
  };

  if (loading) {
    return (
      <div className="dataset-export" style={{ background: colors.background, color: colors.text }}>
        <div className="export-loading">Loading dataset...</div>
      </div>
    );
  }

  if (!dataset) {
    return null;
  }

  const selectedCount = selectedIds.size;
  const totalLabels = submissions
    .filter((sub) => selectedIds.has(sub.id))
    .reduce((sum, sub) => sum + (sub.labelCount || (Array.isArray(sub.labels) ? sub.labels.length : 0)), 0);

  return (
    <div className="dataset-export" style={{ background: colors.background, color: colors.text }}>
      <button
        className="back-button"
        style={{ color: colors.primary }}
        onClick={() => navigate('/library')}
      >
        ← Back to Library
      </button>

      <div className="export-header">
        <div>
          <h1>Export: {dataset.name}</h1>
          {dataset.version && <span className="export-version">v{dataset.version}</span>}
        </div>
        <div className="export-info" style={{ background: colors.card, borderColor: colors.border }}>
          <div className="info-item">
            <span className="info-label">Total Images:</span>
            <span className="info-value">{submissions.length.toLocaleString()}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Total Labels:</span>
            <span className="info-value">
              {submissions.reduce((sum, sub) => sum + (sub.labelCount || (Array.isArray(sub.labels) ? sub.labels.length : 0)), 0).toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert--error" style={{ background: colors.error + '20', color: colors.error }}>
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && downloadUrl && (
        <div className="alert alert--success" style={{ background: colors.success + '20', color: colors.success }}>
          {successMessage}{' '}
          <a href={downloadUrl} target="_blank" rel="noreferrer" style={{ color: colors.success }}>
            Download again
          </a>
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      <div className="export-controls" style={{ borderColor: colors.border }}>
        <button
          className="select-all-btn"
          style={{ borderColor: colors.border, color: colors.text }}
          onClick={selectAll}
        >
          {submissions.every((sub) => selectedIds.has(sub.id)) ? 'Deselect All' : 'Select All'}
        </button>
        <div className="selection-info">
          {selectedCount > 0 && (
            <>
              <span>{selectedCount} selected</span>
              <span>•</span>
              <span>{totalLabels.toLocaleString()} labels</span>
            </>
          )}
        </div>
      </div>

      {submissions.length === 0 ? (
        <div className="empty-state">No submissions found for this dataset.</div>
      ) : (
        <div className="export-table" style={{ borderColor: colors.border }}>
          <table>
            <thead>
              <tr>
                <th />
                <th>Preview</th>
                <th>Task</th>
                <th>Labels</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((sub) => {
                const isSelected = selectedIds.has(sub.id);
                const labelCount = sub.labelCount || (Array.isArray(sub.labels) ? sub.labels.length : 0);
                return (
                  <tr key={sub.id} className={isSelected ? 'row-selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(sub.id)}
                      />
                    </td>
                    <td>
                      {sub.mediaUrl ? (
                        <img src={sub.mediaUrl} alt="" className="table-thumb" />
                      ) : (
                        <div className="table-thumb table-thumb--empty">No image</div>
                      )}
                    </td>
                    <td>{sub.taskTitle || sub.taskName || 'Unknown'}</td>
                    <td>{labelCount.toLocaleString()}</td>
                    <td>
                      <span className="status-badge" style={{ background: colors.success + '20', color: colors.success }}>
                        Ready
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="export-footer" style={{ borderColor: colors.border }}>
        <div className="footer-stats">
          <div>
            <span className="footer-label">Selected:</span>
            <strong>{selectedCount} submissions</strong>
          </div>
          <div>
            <span className="footer-label">Total Labels:</span>
            <strong>{totalLabels.toLocaleString()}</strong>
          </div>
          <div>
            <span className="footer-label">Cost:</span>
            <strong style={{ color: colors.success }}>FREE (Already purchased)</strong>
          </div>
        </div>
        <button
          className="primary-btn"
          style={{ background: colors.primary }}
          disabled={selectedCount === 0 || isExporting}
          onClick={handleExport}
        >
          {isExporting ? 'Processing...' : 'Export Selected'}
        </button>
      </div>
    </div>
  );
}

